import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { ShiftIntoView } from '@/components/ui/shift-into-view';
import { childElementMotionProperties } from './animations';
import { NAVIGATION_ITEMS, type NavigationItem } from './consts';

// Overrides for top-level nav items sitting on the navy header
const headerLinkStyle =
  'text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground focus:bg-primary-foreground/10 focus:text-primary-foreground data-active:bg-primary-foreground/15 data-[state=open]:bg-primary-foreground/15';

/*
 * Renders A Navigation Item
 */
function renderNavigationItem(menuItem: NavigationItem) {
  const hasChildren = !!menuItem.children;

  return (
    <NavigationMenuItem key={menuItem.label}>
      {hasChildren ? (
        <>
          <NavigationMenuTrigger className={headerLinkStyle}>
            {menuItem.label}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ShiftIntoView>
            <div className="w-[520px] p-3">
              {/* Regular items */}
              <ul className="grid gap-1 mb-2">
                {menuItem.children?.filter(c => !c.isCityLink).map(child => (
                  <li key={child.label}>
                    <NavigationMenuLink asChild>
                      <Link
                        href={child.href ?? ''}
                        className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                      >
                        <div className="text-sm font-medium leading-none">{child.label}</div>
                        {child.description && (
                          <p className="mt-1.5 line-clamp-2 text-sm leading-snug text-muted-foreground">
                            {child.description}
                          </p>
                        )}
                      </Link>
                    </NavigationMenuLink>
                  </li>
                ))}
              </ul>

              {/* City links */}
              {menuItem.children?.some(c => c.isCityLink) && (
                <>
                  <div className="border-t border-border my-2" />
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 mb-2">
                    Cities
                  </p>
                  <ul className="grid grid-cols-2 gap-1">
                    {menuItem.children?.filter(c => c.isCityLink).map(child => (
                      <li key={child.label}>
                        <NavigationMenuLink asChild>
                          <Link
                            href={child.href ?? ''}
                            className="block select-none rounded-md px-3 py-2 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">{child.label}</div>
                            {child.description && (
                              <p className="mt-1 text-xs leading-snug text-muted-foreground line-clamp-1">
                                {child.description}
                              </p>
                            )}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {menuItem.viewAllHref && (
                <div className="border-t border-border mt-2 px-3 py-2">
                  <NavigationMenuLink asChild>
                    <Link
                      href={menuItem.viewAllHref}
                      className="block rounded-md p-2 text-sm font-medium text-primary hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      {menuItem.viewAllLabel ?? 'View All'} &rarr;
                    </Link>
                  </NavigationMenuLink>
                </div>
              )}
            </div>
            </ShiftIntoView>
          </NavigationMenuContent>
        </>
      ) : (
        <NavigationMenuLink asChild>
          <Link
            href={menuItem.href ?? ''}
            className={cn(navigationMenuTriggerStyle(), headerLinkStyle)}
          >
            {menuItem.label}
          </Link>
        </NavigationMenuLink>
      )}
    </NavigationMenuItem>
  );
}

function DesktopNavBar() {
  return (
    <motion.div {...childElementMotionProperties}>
      <NavigationMenu>
        <NavigationMenuList>
          {NAVIGATION_ITEMS.map(renderNavigationItem)}
        </NavigationMenuList>
      </NavigationMenu>
    </motion.div>
  );
}

export default DesktopNavBar;
